import { useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import Page from '../../../components/atoms/page';
import TableWidget, { TableColumn } from '../../../components/molecules/tableWidget';
import { fetchInspections, InspectionProcess } from '../../../api/inspections';
import { useSelectedWorkshop } from '../../../context/workshopContext';

const Inspections = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();
  const { selectedOrganisationId } = useSelectedWorkshop();
  const [inspections, setInspections] = useState<InspectionProcess[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || isLoading || !selectedOrganisationId) {
      setInspections([]);
      return;
    }

    const loadInspections = async () => {
      setLoading(true);
      setError(false);

      try {
        const data = await fetchInspections(selectedOrganisationId);
        setInspections(data ?? []);
      } catch (loadError) {
        console.error('Error fetching inspections', loadError);
        setInspections([]);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadInspections();
  }, [isAuthenticated, isLoading, selectedOrganisationId]);

  const columns = useMemo<TableColumn[]>(() => [{ id: 'process', label: 'Process' }], []);

  if (isLoading) return <Box>Loading ...</Box>;

  return (
    <Page title="Inspections">
      {error && <Alert severity="error">Unable to load inspections.</Alert>}
      {loading ? <Box sx={{ py: 3 }}>Loading inspections ...</Box> : (
        <TableWidget
          onClickRow={(event, id, row) => navigate(
            `/manage/inspections/${encodeURIComponent(row?.process as string)}?organisationId=${selectedOrganisationId}`
          )}
          data={inspections.map(({ process }) => ({ id: process, process }))}
          columns={columns}
          defaultSort="process"
          onEdit={(_event, process) => navigate(
            `/manage/inspections/${encodeURIComponent(process)}?organisationId=${selectedOrganisationId}`
          )}
          themes={{ device: 'desktop' }}
          pagination
        />
      )}
    </Page>
  );
};

export default Inspections;
